import { useCallback, useState } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  GripVertical,
  Trash2,
  X,
  Loader2,
  AlertCircle,
  FileText,
  FileImage,
  File,
  Download,
  ExternalLink,
} from 'lucide-react'
import {
  usePropertyDocuments,
  useDeleteDocument,
  useUpdateDocumentOrder,
  type PropertyDocument,
  type DocumentFileType,
} from '@/hooks'
import { formatBytes } from '@/lib/imageCompression'

// ============================================================================
// Constants
// ============================================================================

const DRAG_ACTIVATION_DISTANCE_PX = 8

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get the appropriate icon component for a document file type.
 */
function getDocumentIcon(fileType: DocumentFileType) {
  switch (fileType) {
    case 'pdf':
      return FileText
    case 'doc':
      return FileText
    case 'image':
      return FileImage
    default:
      return File
  }
}

/**
 * Get file type color for badge styling.
 */
function getFileTypeColor(fileType: DocumentFileType): string {
  switch (fileType) {
    case 'pdf':
      return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
    case 'doc':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
    case 'image':
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
    default:
      return 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
  }
}

/**
 * Get file type label in Spanish.
 */
function getFileTypeLabel(fileType: DocumentFileType): string {
  switch (fileType) {
    case 'pdf':
      return 'PDF'
    case 'doc':
      return 'Documento'
    case 'image':
      return 'Imagen'
    default:
      return 'Archivo'
  }
}

// ============================================================================
// Props Interface
// ============================================================================

interface DocumentGalleryProps {
  /** Property ID to manage documents for */
  propertyId: string
  /** Whether the gallery is disabled */
  disabled?: boolean
}

// ============================================================================
// Main Component
// ============================================================================

export function DocumentGallery({ propertyId, disabled = false }: DocumentGalleryProps) {
  const [error, setError] = useState<string | null>(null)
  const [localDocuments, setLocalDocuments] = useState<PropertyDocument[] | null>(null)

  // Queries and mutations
  const { data: documents = [], isLoading: isLoadingDocuments } = usePropertyDocuments(propertyId)
  const deleteMutation = useDeleteDocument()
  const updateOrderMutation = useUpdateDocumentOrder()

  // Use local documents if we have them (during drag), otherwise use server data
  const displayDocuments = localDocuments ?? documents

  // Configure drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: DRAG_ACTIVATION_DISTANCE_PX,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Handle drag end - reorder documents
  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event

      if (!over || active.id === over.id) {
        setLocalDocuments(null)
        return
      }

      const oldIndex = displayDocuments.findIndex((doc: PropertyDocument) => doc.id === active.id)
      const newIndex = displayDocuments.findIndex((doc: PropertyDocument) => doc.id === over.id)

      if (oldIndex === -1 || newIndex === -1) {
        setLocalDocuments(null)
        return
      }

      // Optimistically update local state
      const newOrder = arrayMove(displayDocuments, oldIndex, newIndex) as PropertyDocument[]
      setLocalDocuments(newOrder)

      // Prepare order items for API
      const orderItems = newOrder.map((doc: PropertyDocument, index: number) => ({
        id: doc.id,
        sort_order: index,
      }))

      try {
        await updateOrderMutation.mutateAsync({
          items: orderItems,
          propertyId,
        })
        setError(null)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error al actualizar el orden de documentos')
        // Revert to server state
        setLocalDocuments(null)
      } finally {
        // Clear local state after successful update
        setLocalDocuments(null)
      }
    },
    [displayDocuments, propertyId, updateOrderMutation]
  )

  // Handle delete with confirmation
  const handleDelete = useCallback(
    async (document: PropertyDocument) => {
      if (!confirm(`¿Eliminar "${document.name}"? Esta acción no se puede deshacer.`)) {
        return
      }

      try {
        await deleteMutation.mutateAsync({
          id: document.id,
          propertyId,
        })
        setError(null)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error al eliminar documento')
      }
    },
    [deleteMutation, propertyId]
  )

  const isBusy = updateOrderMutation.isPending || deleteMutation.isPending

  // Loading state
  if (isLoadingDocuments) {
    return (
      <section className="rounded-lg border bg-card p-6">
        <h2 className="mb-4 text-lg font-semibold">Galería de Documentos</h2>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </section>
    )
  }

  // Empty state
  if (displayDocuments.length === 0) {
    return (
      <section className="rounded-lg border bg-card p-6">
        <h2 className="mb-4 text-lg font-semibold">Galería de Documentos</h2>
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <FileText className="h-16 w-16 text-muted-foreground/30" />
          <p className="mt-4 text-muted-foreground">Aún no hay documentos subidos</p>
          <p className="mt-1 text-sm text-muted-foreground/70">
            Usa el cargador de arriba para añadir documentos
          </p>
        </div>
      </section>
    )
  }

  return (
    <section className="rounded-lg border bg-card p-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Galería de Documentos</h2>
        <p className="text-sm text-muted-foreground">
          {displayDocuments.length} documento{displayDocuments.length !== 1 ? 's' : ''} • Arrastra para reordenar
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-medium">Error</p>
            <p>{error}</p>
          </div>
          <button
            type="button"
            onClick={() => setError(null)}
            className="flex-shrink-0 p-1 hover:opacity-70"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Sortable Document List */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={displayDocuments.map((doc: PropertyDocument) => doc.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {displayDocuments.map((document: PropertyDocument) => (
              <SortableDocumentCard
                key={document.id}
                document={document}
                onDelete={() => handleDelete(document)}
                isDeleting={
                  deleteMutation.isPending &&
                  deleteMutation.variables?.id === document.id
                }
                disabled={disabled || isBusy}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </section>
  )
}

// ============================================================================
// Sortable Document Card Component
// ============================================================================

interface SortableDocumentCardProps {
  document: PropertyDocument
  onDelete: () => void
  isDeleting: boolean
  disabled: boolean
}

function SortableDocumentCard({
  document,
  onDelete,
  isDeleting,
  disabled,
}: SortableDocumentCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: document.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.8 : 1,
  }

  const Icon = getDocumentIcon(document.file_type)
  const colorClass = getFileTypeColor(document.file_type)
  const typeLabel = getFileTypeLabel(document.file_type)

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        group flex items-center gap-3 rounded-lg border bg-background p-3 shadow-sm transition-all
        ${isDragging ? 'ring-2 ring-primary shadow-lg' : ''}
        ${disabled ? 'opacity-50' : ''}
        ${!disabled ? 'hover:bg-muted/50' : ''}
      `}
    >
      {/* Drag Handle */}
      <button
        type="button"
        className={`
          flex-shrink-0 rounded p-1.5 text-muted-foreground
          transition-colors hover:bg-muted hover:text-foreground
          ${disabled ? 'cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'}
        `}
        {...attributes}
        {...listeners}
        disabled={disabled}
        title="Arrastra para reordenar"
      >
        <GripVertical className="h-4 w-4" />
      </button>

      {/* Document Icon */}
      <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${colorClass}`}>
        <Icon className="h-5 w-5" />
      </div>

      {/* Document Info */}
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium" title={document.name}>
          {document.name}
        </p>
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span className={`rounded px-1.5 py-0.5 font-medium ${colorClass}`}>
            {typeLabel}
          </span>
          {document.file_size && (
            <span>{formatBytes(document.file_size)}</span>
          )}
          {document.description && (
            <span className="truncate max-w-[150px]" title={document.description}>
              {document.description}
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
        {/* Open in New Tab */}
        <a
          href={document.url}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
          title="Abrir en nueva pestaña"
        >
          <ExternalLink className="h-4 w-4" />
        </a>

        {/* Download Link */}
        <a
          href={document.url}
          download={document.name}
          className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
          title="Descargar documento"
        >
          <Download className="h-4 w-4" />
        </a>

        {/* Delete Button */}
        <button
          type="button"
          onClick={onDelete}
          disabled={disabled || isDeleting}
          className="rounded-lg p-2 text-red-500 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-red-900/20"
          title="Eliminar documento"
        >
          {isDeleting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
        </button>
      </div>
    </div>
  )
}
